import type { SearchTagsResponseItem, Tag } from "@/shared/api/newsquawk-headlines";

/**
 * Ranking for tag-library search results.
 *
 * `GET /tags/search/{q}` is a substring match returned in tag-id order with no
 * relevance score, so the tag the user meant is routinely not near the top:
 * searching "OPEC" returns twelve hits with the actual OPEC tag tenth, behind
 * seven Sinopec subsidiaries. Every hit contains the query somewhere, so the
 * useful distinction is *where* — which is what `labelMatch` reads.
 *
 * Nothing is dropped. A weak match is still a match the user asked for; it just
 * sorts below the ones that answer the query head-on.
 */

export type MatchStrength = "exact" | "leading" | "weak";

/**
 * How well one label answers the term.
 *
 * "leading" means the term is the opening whole word of the label: "Apple" in
 * "Apple Inc", but not "Cook" in "Thomas Cook Group PLC" (not leading) nor in
 * "COOKPAD" (not a whole word) nor "apple" in "Dr Pepper Snapple" (neither).
 * That single distinction is what separates the term the user meant from the
 * substring collisions the search returns alongside it.
 */
export function labelMatch(term: string, label: string): MatchStrength {
  const wanted = term.trim().toLowerCase();
  const name = label.toLowerCase();
  if (name === wanted) return "exact";
  if (name.startsWith(wanted) && /[^a-z0-9]/.test(name.charAt(wanted.length))) {
    return "leading";
  }
  return "weak";
}

export interface RankedTag extends Tag {
  /** The label that actually matched, which may not be the primary one. */
  matchedLabel: string;
  strength: MatchStrength;
  /**
   * What the tag is — company, ticker, term… (TagCategory). The builder's
   * Tags box shows it as the result's kind and leaves ticker tags out.
   */
  category?: string;
}

const STRENGTH_ORDER: Record<MatchStrength, number> = {
  exact: 0,
  leading: 1,
  weak: 2,
};

/**
 * Ranks raw `/tags/search` hits best-first, one entry per tag.
 *
 * A tag carries several labels (AAPL / AAPL.OQ / its ISIN), so it is scored on
 * its own best-matching one and listed under the name a reader recognises: the
 * primary label, falling back to whichever label matched.
 */
export function rankTagHits(
  query: string,
  hits: SearchTagsResponseItem[]
): RankedTag[] {
  const term = query.trim();
  if (!term) return [];

  const best = new Map<number, RankedTag>();

  for (const hit of hits) {
    for (const label of hit.labels ?? []) {
      if (!label?.name) continue;
      const strength = labelMatch(term, label.name);
      const primary = hit.labels?.find((l) => l.primary) ?? hit.labels?.[0];
      const candidate: RankedTag = {
        tag_id: hit.id,
        name: primary?.name ?? label.name,
        score: primary?.scanning_importance ?? 1.0,
        matchedLabel: label.name,
        strength,
        category: hit.category,
      };
      const existing = best.get(hit.id);
      if (
        !existing ||
        STRENGTH_ORDER[strength] < STRENGTH_ORDER[existing.strength] ||
        (STRENGTH_ORDER[strength] === STRENGTH_ORDER[existing.strength] &&
          label.name.length < existing.matchedLabel.length)
      ) {
        best.set(hit.id, candidate);
      }
    }
  }

  // Shortest matched label wins a tie: "Apple Inc" over "Apple Flavor &
  // Fragrance Group Co Ltd" — the least additional wording is the closest read.
  return [...best.values()].sort(
    (a, b) =>
      STRENGTH_ORDER[a.strength] - STRENGTH_ORDER[b.strength] ||
      a.matchedLabel.length - b.matchedLabel.length ||
      a.name.localeCompare(b.name)
  );
}
